/**
 * Cost rates and constants for RumahKira estimation engine
 * All rates are in Malaysian Ringgit (RM) based on 2024-2026 market rates
 */

// Cost per square foot by finishing level (RM/sqft)
export const COST_RATES = {
  basic: {
    min: 80,
    max: 120,
    label: 'Basic',
    labelMy: 'Asas',
    description: 'Essential finishes only',
    descriptionMy: 'Kemasan asas sahaja',
    features: [
      'Standard cement flooring',
      'Basic paint & plastering',
      'Simple fixtures & fittings',
      'Basic electrical & plumbing'
    ],
    featuresMy: [
      'Lantai simen standard',
      'Cat & plaster asas',
      'Kelengkapan mudah',
      'Elektrik & paip asas'
    ]
  },
  standard: {
    min: 120,
    max: 180,
    label: 'Standard',
    labelMy: 'Standard',
    description: 'Good quality, durable finishes',
    descriptionMy: 'Kemasan berkualiti dan tahan lama',
    features: [
      'Ceramic floor tiles',
      'Quality paint & finishes',
      'Standard fittings & fixtures',
      'Complete electrical & plumbing'
    ],
    featuresMy: [
      'Jubin lantai seramik',
      'Cat & kemasan berkualiti',
      'Kelengkapan standard',
      'Elektrik & paip lengkap'
    ],
    recommended: true
  },
  premium: {
    min: 180,
    max: 280,
    label: 'Premium',
    labelMy: 'Premium',
    description: 'High-end materials & finishes',
    descriptionMy: 'Bahan & kemasan mewah',
    features: [
      'Porcelain/marble tiles',
      'Premium fixtures & fittings',
      'Quality built-in cabinetry',
      'High-end electrical & plumbing'
    ],
    featuresMy: [
      'Jubin porselin/marmar',
      'Kelengkapan premium',
      'Kabinet terbina berkualiti',
      'Elektrik & paip mewah'
    ]
  }
};

// House types with multipliers
export const HOUSE_TYPES = {
  kampung: {
    label: 'Kampung House',
    labelMy: 'Rumah Kampung',
    description: 'Traditional timber/concrete structure',
    descriptionMy: 'Struktur kayu/konkrit tradisional',
    multiplier: 0.85, // Slightly lower cost due to simpler structure
    wallRatio: 0.7, // Less external walls
    icon: 'kampung'
  },
  terrace: {
    label: 'Terrace House',
    labelMy: 'Rumah Teres',
    description: 'Standard linked house',
    descriptionMy: 'Rumah teres bersambung standard',
    multiplier: 1.0, // Base rate
    wallRatio: 0.6, // Shared walls reduce cost
    icon: 'terrace'
  },
  bungalow: {
    label: 'Bungalow',
    labelMy: 'Banglo',
    description: 'Detached house, all sides exposed',
    descriptionMy: 'Rumah sesebuah, semua sisi terbuka',
    multiplier: 1.15, // Higher cost due to more external walls
    wallRatio: 1.0, // All walls exposed
    icon: 'bungalow'
  }
};

// Floor multipliers: cost of an N-storey house relative to a single storey of the same footprint
export const FLOOR_MULTIPLIERS = {
  1: 1.0,
  2: 1.85, // Not quite double due to shared foundation
  3: 2.65
};

/**
 * Cost-per-sqft factor for a house with the given number of floors.
 * Built-up area is the TOTAL of all floors, so a 2-storey house costs 1.85× its footprint,
 * i.e. 0.925× per sqft of built-up area (shared foundation and roof).
 */
export const getFloorCostFactor = (floors) => {
  const n = Math.min(Math.max(parseInt(floors, 10) || 1, 1), 3);
  return FLOOR_MULTIPLIERS[n] / n;
};

// Budget allocation breakdown (percentage)
export const BUDGET_ALLOCATION = {
  structure: {
    percentage: 65,
    label: 'Structural Works',
    labelMy: 'Kerja Struktur',
    items: [
      'Foundation & Ground Beam',
      'Columns & Beams',
      'Roof Structure',
      'Walls & Partitions'
    ],
    itemsMy: [
      'Asas & Rasuk Tanah',
      'Tiang & Rasuk',
      'Struktur Bumbung',
      'Dinding & Sekatan'
    ]
  },
  finishes: {
    percentage: 35,
    label: 'Finishing Works',
    labelMy: 'Kerja Kemasan',
    items: [
      'Plastering & Painting',
      'Floor & Wall Tiles',
      'Doors & Windows',
      'Electrical & Plumbing'
    ],
    itemsMy: [
      'Plaster & Cat',
      'Jubin Lantai & Dinding',
      'Pintu & Tingkap',
      'Elektrik & Paip'
    ]
  }
};

// Material estimation ratios (per 100 sqft of built-up area)
export const MATERIAL_RATIOS = {
  cement: {
    bags: 23, // 50kg bags per 100 sqft
    label: 'Cement',
    labelMy: 'Simen',
    unit: 'bags (50kg)',
    unitMy: 'beg (50kg)'
  },
  bricks: {
    units: 710, // bricks per 100 sqft
    label: 'Bricks',
    labelMy: 'Batu Bata',
    unit: 'units',
    unitMy: 'unit'
  },
  steel: {
    tons: 0.2, // tons per 100 sqft
    label: 'Steel Reinforcement',
    labelMy: 'Tetulang Keluli',
    unit: 'tons',
    unitMy: 'tan'
  },
  sand: {
    cubicMeters: 3.8, // cubic meters per 100 sqft
    label: 'Sand',
    labelMy: 'Pasir',
    unit: 'm³',
    unitMy: 'm³'
  },
  gravel: {
    cubicMeters: 2.9, // cubic meters per 100 sqft
    label: 'Gravel',
    labelMy: 'Batu Kerikil',
    unit: 'm³',
    unitMy: 'm³'
  },
  tiles: {
    sqft: 1.12, // sqft of tiles per sqft of built-up (includes wastage)
    label: 'Floor Tiles',
    labelMy: 'Jubin Lantai',
    unit: 'sq ft',
    unitMy: 'kaki persegi'
  },
  paint: {
    liters: 0.07, // liters per sqft (walls, internal)
    label: 'Paint',
    labelMy: 'Cat',
    unit: 'liters',
    unitMy: 'liter'
  }
};

// Finishing level material multipliers
export const FINISHING_MATERIAL_MULTIPLIERS = {
  basic: {
    tiles: 0.6, // Less tiling, more cement floors
    paint: 0.8
  },
  standard: {
    tiles: 1.0,
    paint: 1.0
  },
  premium: {
    tiles: 1.2, // More tiling coverage
    paint: 1.2 // More coats, better coverage
  }
};

// Assumptions and exclusions
export const ASSUMPTIONS = {
  included: [
    { en: 'Flat, stable land (no slope)', my: 'Tanah rata dan stabil (tiada cerun)' },
    { en: 'Standard reinforced concrete frame', my: 'Kerangka konkrit bertetulang standard' },
    { en: 'Conventional construction method', my: 'Kaedah pembinaan konvensional' },
    { en: 'Average material prices in Malaysia', my: 'Harga bahan purata di Malaysia' },
    { en: 'Standard ceiling height (10 ft)', my: 'Ketinggian siling standard (10 kaki)' }
  ],
  excluded: [
    { en: 'Land clearing & earthworks', my: 'Pembersihan tanah & kerja tanah' },
    { en: 'Piling or special foundation', my: 'Cerucuk atau asas khas' },
    { en: 'Fencing & external landscaping', my: 'Pagar & landskap luaran' },
    { en: 'Built-in furniture & cabinetry', my: 'Perabot terbina & kabinet' },
    { en: 'Professional fees (architect, engineer)', my: 'Fi profesional (arkitek, jurutera)' },
    { en: 'Permit & submission fees', my: 'Fi permit & penyerahan' }
  ]
};

// Disclaimer text
export const DISCLAIMER = {
  en: {
    title: 'Important Disclaimer',
    content: [
      'This is a PRELIMINARY ESTIMATION for planning purposes only.',
      'Material quantities are shown as a ±12% range. Actual construction costs may vary depending on site conditions, material prices, and contractor rates.',
      'This is NOT a quotation or contract.',
      'Please consult a licensed contractor or quantity surveyor for accurate project costing.'
    ],
    footer: 'RumahKira is intended to help you plan your budget, not replace professional advice.'
  },
  my: {
    title: 'Penafian Penting',
    content: [
      'Ini adalah ANGGARAN AWAL untuk tujuan perancangan sahaja.',
      'Kuantiti bahan ditunjukkan sebagai julat ±12%. Kos pembinaan sebenar mungkin berbeza bergantung pada keadaan tapak, harga bahan, dan kadar kontraktor.',
      'Ini BUKAN sebut harga atau kontrak.',
      'Sila rujuk kontraktor berlesen atau juruukur bahan untuk kos projek yang tepat.'
    ],
    footer: 'RumahKira bertujuan membantu anda merancang bajet, bukan menggantikan nasihat profesional.'
  }
};
